/* eslint-disable react-hooks/exhaustive-deps */
import { FC, useEffect, useState } from 'react';
import { GetServerSideProps } from 'next';
import { dehydrate, QueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import queryString from 'query-string';

import dynamic from 'next/dynamic';
import { ProviderDetails, ServicesOffered, GlobalContentLayout } from '@/components';
import { PageProvider } from '@/context/PageContext';
import useServicesState from '@/hooks/useServicesState';
import { useShowComponent } from '@/hooks/showComponent';

import * as St from './ServiceProvider.styles';
import { ServiceProviderPageProps } from './ServiceProvider.types';
import { BranchOfficeHeader } from '../BranchOffice/components/BranchOfficeHeader';
import { getCompanyOverview } from '@/services/company/company.repo';
import { getFilteredProvider } from '@/services/serviceProviders/serviceProviders.repo';
import { getLocation } from '@/services/locations/locations.repo';
import { ServiceModel } from '@/services/services';
import usePageContext from '@/hooks/usePageContext';

const ActivationBar = dynamic(() => import('@/components/ActivationBar/ActivationBar'));

const ServiceProvider: FC<ServiceProviderPageProps> = ({
  locationId,
  filteredProvider,
  companyOverview,
  currentLocation,
  companySlug,
  queryParams,
}) => {
  const { t } = useTranslation();
  const { state, dispatch } = useServicesState(filteredProvider.services);
  const { hide: hideCart, show: showCart, isShown: isCartShown } = useShowComponent();
  const [wiggle, setWiggle] = useState(false);
  const [selected, setSelected] = useState<Array<ServiceModel>>([]);
  const { setSelectedServices } = usePageContext();

  useEffect(() => {
    if (setSelectedServices) {
      setSelectedServices(!!selected.length);
    }
  }, [selected]);

  return (
    <PageProvider
      companySlug={companySlug}
      companyOverview={companyOverview}
      locationId={locationId}
      nextSeo={{
        title: `${filteredProvider.publicName} ${t('SEO.IN')} ${companyOverview.name} | ${t('SEO.BOOK_ONLINE')}`,
        description: `${t('SEO.PROVIDERS_DESCRIPTION')} ${filteredProvider.publicName} ${t('SEO.IN')} ${
          companyOverview.name
        } - ${currentLocation.locationName}`,
      }}
      queryParams={queryParams}
    >
      <BranchOfficeHeader />
      <GlobalContentLayout>
        {!companyOverview?.minisiteV2Active && <ActivationBar />}

        <St.Wrapper>
          <ProviderDetails
            name={filteredProvider!.publicName}
            schedule={filteredProvider!.providerTimes}
            imageUrl={filteredProvider!.image}
          />
          <ServicesOffered
            services={state}
            dispatch={dispatch}
            serviceProvider={filteredProvider}
            selected={selected}
            setSelected={(items: ServiceModel[]) => setSelected(items)}
            setWiggle={setWiggle}
            isCartShown={isCartShown}
            hideCart={hideCart}
            showCart={showCart}
          />
        </St.Wrapper>
      </GlobalContentLayout>
    </PageProvider>
  );
};

export default ServiceProvider;

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const companySlug = ctx.req.headers.host?.split('.')[0];
  const { countryCode, locationId, providerId, token, ...params } = ctx.query as {
    providerId: string;
    countryCode: string;
    locationId: string;
    token: string | undefined;
  };
  const queryClient = new QueryClient();

  try {
    const companyOverview = await queryClient.fetchQuery(
      ['getCompanyOverview', companySlug, countryCode, locationId],
      ({ queryKey }) => getCompanyOverview(queryKey[1], queryKey[2], queryKey[3]),
    );

    const currentLocation = await queryClient.fetchQuery(['getLocation', locationId], ({ queryKey }) =>
      getLocation(parseInt(queryKey[1], 10)),
    );

    const keyToken = token || '';
    const filteredProvider = await queryClient.fetchQuery({
      queryKey: ['getFilteredProvider', locationId, providerId, keyToken],
      queryFn: ({ queryKey }) =>
        getFilteredProvider({
          locationId: queryKey[1],
          id: queryKey[2],
          charlyToken: queryKey[3],
        }),
    });

    const query = queryString.stringify(params);
    const queryParams = query ? `?${query}` : '';

    if (!filteredProvider.length || !filteredProvider[0].services.length || companyOverview.showProviders === 2) {
      return {
        redirect: {
          permanent: false,
          destination: `/${countryCode}/sucursal/${locationId}/${queryParams}`,
        },
      };
    }
    return {
      props: {
        companySlug,
        dehydratedState: dehydrate(queryClient),
        locationId,
        currentLocation,
        companyOverview,
        filteredProvider: filteredProvider[0],
        queryParams,
      },
    };
  } catch (e) {
    return {
      redirect: {
        permanent: false,
        destination: 'https://agendapro.com/',
      },
    };
  }
};
